import React, { createContext, useContext, useEffect, useState } from "react";
import LaboratoryResponse from "../api/models/LaboratoryResponse";
import TestRequestPurpose from "../api/models/test-request/TestRequestPurpose";
import LaboratoriesApi from "../api/services/LaboratoriesApi";
import useEnv from "../hooks/env";
import Dictionary from "../models/Dictionary";
import CSCLaboratory from "../pages/Admin/AdminUserOverview/CSCLaboratory";
import ErrorSnackbarContext from "./ErrorSnackbarContext";

type LaboratoriesContextType = {
  labs: LaboratoryResponse[];
  labsDictionary: Dictionary<CSCLaboratory> | undefined;
  labsReverseDictionary: Dictionary<string, CSCLaboratory> | undefined;
  labsLoading: boolean;
};
type LaboratoriesProviderProps = {
  children: React.ReactNode;
};

function makeDictionaries(
  labResponse: LaboratoryResponse[],
): [Dictionary<CSCLaboratory>, Dictionary<string, CSCLaboratory>] {
  return labResponse.reduce(
    (dicts, lab) => {
      dicts[0][lab.key] = lab.name;
      dicts[1][lab.name] = lab.key;
      return dicts;
    },
    [{}, {}] as [Dictionary<CSCLaboratory>, Dictionary<string, CSCLaboratory>],
  );
}

const LaboratoriesContext = createContext<LaboratoriesContextType | undefined>(undefined);

function LaboratoriesProvider({ children }: LaboratoriesProviderProps) {
  const { api } = useEnv();
  const labsApi = new LaboratoriesApi(api);
  const [labs, setLabs] = useState<LaboratoryResponse[]>([]);
  const [labsDictionary, setLabsDictionary] = useState<Dictionary<CSCLaboratory>>();
  const [labsReverseDictionary, setLabsReverseDictionary] = useState<Dictionary<string, CSCLaboratory>>();
  const [labsLoading, setLabsLoading] = useState(false);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const value = { labs, labsLoading, labsDictionary, labsReverseDictionary };

  useEffect(() => {
    setLabsLoading(true);
    labsApi
      .fetchLaboratories()
      .then(({ data }) => {
        const [dictionary, reverseDictionary] = makeDictionaries(data);
        setLabs(data);
        setLabsDictionary(dictionary);
        setLabsReverseDictionary(reverseDictionary);
      })
      .catch(handleHttpError)
      .finally(() => setLabsLoading(false));
  }, []);

  return <LaboratoriesContext.Provider value={value}>{children}</LaboratoriesContext.Provider>;
}

function useLaboratories(purpose?: TestRequestPurpose) {
  const context = useContext(LaboratoriesContext);
  if (context === undefined) {
    throw new Error("useLaboratories must be used within a LaboratoriesProvider");
  }
  const { labs } = context;
  const labsFiltered = purpose ? labs.filter((l) => l.purpose === purpose) : null;
  return {
    ...context,
    labs: labsFiltered || labs,
    labsTechnical: labsFiltered?.map((lf) => lf.key) || labs.map((l) => l.key),
  };
}

export { LaboratoriesProvider, useLaboratories };
